// exerciseCanonical — name aliasing + auto-learn for the exercise log.
//
// Steve (or any coach) sends workouts in WhatsApp with slightly varied
// names: "Dumbbell Goblet Squats" / "DB Goblet Squat" / "Goblet Squats".
// Without normalisation, the PR / history aggregator (which groups by
// ex.name) splits those into three separate stat lines and the user
// loses progressive-overload tracking.
//
// This module:
//   1. resolveExerciseId(name, providedId) → canonical id from the static
//      EXERCISE_LIBRARY OR the user's localStorage custom library OR a
//      derived stable slug for genuinely-new exercises.
//   2. resolveExerciseName(name, providedId) → the display name attached
//      to the canonical id (so the UI shows the library's preferred form
//      "DB Goblet Squat" even when the user typed "Dumbbell Goblet
//      Squats").
//   3. resolveExerciseEntry(name, providedId) → full library/custom row
//      (with primary / muscles / unit / defaults) so callers can pull
//      metadata once.
//   4. learnExercise(meta) → upserts an unknown exercise into the
//      localStorage custom library so the NEXT log of the same name
//      gets the same canonical id.
//
// Resolution order:
//   a) providedId is one of EXERCISE_LIBRARY's ids → use it
//   b) providedId is in the localStorage custom library → use it
//   c) normalised name matches a library entry's normalised name → use that id
//   d) normalised name matches one of ALIASES → use that id
//   e) normalised name matches a custom-library entry → use that id
//   f) (after singular/plural + "DB"/"Dumbbell" swap attempts) → use any match
//   g) derive a stable slug `custom_<normalised>` so future logs of the
//      same string resolve identically.

import { EXERCISE_LIBRARY } from "./riyadahData";

const CUSTOM_LIB_KEY = "riyadahCustomExerciseLibrary";

// Manually-curated alias table. Add an entry here whenever a coach
// sends a workout with a name we haven't seen before that maps cleanly
// to an existing library exercise. Keys are lowercase / punctuation-
// stripped / collapsed whitespace.
const ALIASES = {
  // Goblet squat family — every plausible Steve / Yusuf phrasing.
  // Sumo goblet folds into the same row (variant of goblet); pure
  // "DB Sumo Squat" stays a separate exercise via the library entry.
  "goblet":                          "db_goblet_squat",
  "goblet squat":                    "db_goblet_squat",
  "goblet squats":                   "db_goblet_squat",
  "dumbbell goblet squat":           "db_goblet_squat",
  "dumbbell goblet squats":          "db_goblet_squat",
  "db goblet squats":                "db_goblet_squat",
  "sumo goblet squat":               "db_goblet_squat",
  "sumo goblet squats":              "db_goblet_squat",
  "dumbbell sumo goblet squat":      "db_goblet_squat",
  "kettlebell goblet squat":         "db_goblet_squat",
  "kb goblet squat":                 "db_goblet_squat",
  "heavy goblet squat":              "db_goblet_squat",
  // Shoulder press
  "shoulder press":                  "db_shoulder_press",
  "dumbbell shoulder press":         "db_shoulder_press",
  "db shoulder press standing":      "db_shoulder_press",
  "seated dumbbell shoulder press":  "db_shoulder_press",
  "seated db shoulder press":        "db_shoulder_press",
  // Bent-over row
  "bent over row":                   "db_bent_row",
  "bent over rows":                  "db_bent_row",
  "dumbbell bent over rows":         "db_bent_row",
  "dumbbell bent over row":          "db_bent_row",
  "db bent over row":                "db_bent_row",
  "db bent over rows":               "db_bent_row",
  "db row":                          "db_bent_row",
  "dumbbell row":                    "db_bent_row",
  "dumbbell rows":                   "db_bent_row",
  // Hammer curl
  "hammer curl":                     "db_hammer_curl",
  "hammer curls":                    "db_hammer_curl",
  "dumbbell hammer curls":           "db_hammer_curl",
  "dumbbell hammer curl":            "db_hammer_curl",
  "db hammer curls":                 "db_hammer_curl",
  // Bicep curl
  "bicep curl":                      "db_curl",
  "bicep curls":                     "db_curl",
  "db curl":                         "db_curl",
  "db curls":                        "db_curl",
  "db bicep curl":                   "db_curl",
  "db bicep curls":                  "db_curl",
  "dumbbell curl":                   "db_curl",
  "dumbbell curls":                  "db_curl",
  "dumbbell bicep curl":             "db_curl",
  "dumbbell bicep curls":            "db_curl",
  // Lateral raise
  "lateral raise":                   "db_lateral_raise",
  "lateral raises":                  "db_lateral_raise",
  "dumbbell lateral raise":          "db_lateral_raise",
  "dumbbell lateral raises":         "db_lateral_raise",
  "db lateral raise":                "db_lateral_raise",
  "db side raise":                   "db_lateral_raise",
  "side raise":                      "db_lateral_raise",
  // Chest press machine
  "chest press":                     "chest_press_machine",
  "chest press machine":             "chest_press_machine",
  "seated chest press":              "chest_press_machine",
  "machine chest press":             "chest_press_machine",
  // Seated row
  "seated row":                      "seated_row",
  "seated rows":                     "seated_row",
  "seated cable row":                "seated_row",
  "seated row machine":              "seated_row",
  "cable row":                       "seated_row",
  "row machine":                     "seated_row",
  // Leg press
  "leg press":                       "leg_press",
  "leg press machine":               "leg_press",
  // Leg extension
  "leg extension":                   "leg_extension",
  "leg extensions":                  "leg_extension",
  "leg extension machine":           "leg_extension",
  // Lat pulldown
  "lat pulldown":                    "lat_pulldown",
  "lat pull down":                   "lat_pulldown",
  "lat pulldowns":                   "lat_pulldown",
  "pulldown":                        "lat_pulldown",
  // Walking lunge
  "walking lunge":                   "db_lunge",
  "walking lunges":                  "db_lunge",
  "dumbbell walking lunge":          "db_lunge",
  "dumbbell walking lunges":         "db_lunge",
  "db walking lunge":                "db_lunge",
  "db walking lunges":               "db_lunge",
  // Tricep kickback
  "tricep kickback":                 "db_kickback",
  "tricep kickbacks":                "db_kickback",
  "dumbbell tricep kickback":        "db_kickback",
  "dumbbell tricep kickbacks":       "db_kickback",
  "db tricep kickback":              "db_kickback",
  "db tricep kickbacks":             "db_kickback",
  // Stiff-leg deadlift
  "stiff leg deadlift":              "db_sldl",
  "stiff legged deadlift":           "db_sldl",
  "stiff legged deadlifts":          "db_sldl",
  "stiff leg dl":                    "db_sldl",
  "sldl":                            "db_sldl",
  "rdl":                             "db_sldl",
  "romanian deadlift":               "db_sldl",
  "romanian deadlifts":              "db_sldl",
  // Stiff-leg DL + upright row combo
  "stiff legged deadlift into upright row":           "db_sldl_upright_row",
  "stiff leg deadlift into upright row":              "db_sldl_upright_row",
  "stiff leg dl + upright row":                       "db_sldl_upright_row",
  "stiff legged deadlift into upright row dumbbells": "db_sldl_upright_row",
  // Overhead tricep
  "overhead tricep extension":          "db_oh_tricep",
  "overhead tricep extensions":         "db_oh_tricep",
  "overhead triceps extension":         "db_oh_tricep",
  "dumbbell overhead tricep extension": "db_oh_tricep",
  "db overhead tricep ext":             "db_oh_tricep",
  // Flat bench
  "flat bench":                      "bb_bench",
  "flat barbell bench press":        "bb_bench",
  "barbell bench press":             "bb_bench",
  "bench press":                     "bb_bench",
};

// Words that don't change which exercise it is — "Heavy", "Warm-Up",
// "Drop Set", "Superset", weight suffixes ("5kg", "10lb"), set
// modifiers. Stripped before name matching so historical entries with
// fluff prefixes/suffixes still collapse into the canonical row.
const MODIFIER_WORDS = new Set([
  "heavy", "light", "moderate", "easy", "hard",
  "warmup", "warm", "cooldown", "cool",
  "dropset", "drop", "superset", "super", "giantset", "giant",
  "amrap", "emom", "tabata", "rest", "pause", "tempo",
  "set", "sets", "round", "rounds", "rep", "reps",
  "with", "the", "and", "of", "for", "to", "a", "an",
]);
// Word-level synonyms applied after tokenisation. Both variants
// collapse to the same form so e.g. "dl" and "deadlift" hash equal.
const SYNONYMS = {
  "db": "dumbbell",
  "bb": "barbell",
  "kb": "kettlebell",
  "bw": "bodyweight",
  "oh": "overhead",
  "dl": "deadlift",
  "rdl": "romaniandeadlift",
  "sldl": "stiffleggeddeadlift",
  "ohp": "overheadpress",
  "ext": "extension",
  "ext.": "extension",
  "extension": "extension",
  "extensions": "extension",
  "curl": "curl",
  "curls": "curl",
  "row": "row",
  "rows": "row",
  "raise": "raise",
  "raises": "raise",
  "press": "press",
  "pressing": "press",
  "squat": "squat",
  "squats": "squat",
  "lunge": "lunge",
  "lunges": "lunge",
  "fly": "fly",
  "flies": "fly",
  "flys": "fly",
  "kickback": "kickback",
  "kickbacks": "kickback",
  "pulldown": "pulldown",
  "pulldowns": "pulldown",
};

// Normalise a name to a comparable key — lowercase, ASCII-only, single
// spaces. Punctuation, parentheticals (e.g. "(Heavy)"), and units fall
// away so "Leg Press (Heavy)" and "leg press" hash to the same key.
function normName(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\(.*?\)/g, " ")           // drop parentheticals
    .replace(/\d+(\.\d+)?\s*(kg|lb|lbs|pound|pounds|reps?)\b/g, " ") // strip weight/rep suffixes "5kg" / "10 lb" / "12 reps"
    .replace(/[^a-z0-9]+/g, " ")        // punctuation → space
    .replace(/\s+/g, " ")
    .trim();
}
// Deeper canonicalisation — applies synonym map + drops modifier words
// + sorts tokens. So "Heavy Sumo Goblet Squat DB" and "DB Goblet Squat"
// both become the same hash. ONLY use for ambiguous matches AFTER the
// literal-name and curated-alias lookups fail.
function deepNormName(s) {
  const tokens = normName(s)
    .split(/\s+/)
    .filter(Boolean)
    .map(t => SYNONYMS[t] || t)
    .filter(t => !MODIFIER_WORDS.has(t));
  // Sort so word order doesn't matter ("Goblet DB Squat" = "DB Goblet Squat").
  return tokens.sort().join(" ");
}

// Build static lookup tables once.
const STATIC_NAME_TO_ID = (() => {
  const m = new Map();
  for (const e of EXERCISE_LIBRARY) m.set(normName(e.name), e.id);
  for (const [k, v] of Object.entries(ALIASES)) m.set(normName(k), v);
  return m;
})();
// Deep normalisation tier — sort-words + synonyms + modifier-stripped.
// Caught last in the resolver chain so the safer literal/alias paths
// always win first.
const STATIC_DEEP_NAME_TO_ID = (() => {
  const m = new Map();
  for (const e of EXERCISE_LIBRARY) {
    const k = deepNormName(e.name);
    if (k && !m.has(k)) m.set(k, e.id);
  }
  for (const [alias, id] of Object.entries(ALIASES)) {
    const k = deepNormName(alias);
    if (k && !m.has(k)) m.set(k, id);
  }
  return m;
})();
const STATIC_ID_TO_ENTRY = (() => {
  const m = new Map();
  for (const e of EXERCISE_LIBRARY) m.set(e.id, e);
  return m;
})();

// Manual override map — populated by the "Merge into…" UI in
// ExerciseDetail. Persists in localStorage so user-confirmed merges
// survive across launches. Shape: { sourceCanonicalId: targetCanonicalId }
const MANUAL_KEY = "riyadahManualAliases";
function loadManualMap() {
  try {
    const raw = localStorage.getItem(MANUAL_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return (obj && typeof obj === "object") ? obj : {};
  } catch { return {}; }
}
function saveManualMap(map) {
  try { localStorage.setItem(MANUAL_KEY, JSON.stringify(map)); } catch {}
}
// Follow the chain of manual aliases (A → B → C → ...) so a transitive
// merge resolves all the way through. Cycles are guarded with a depth
// cap.
function followManualAlias(id) {
  const map = loadManualMap();
  let cur = id;
  for (let i = 0; i < 8 && map[cur]; i++) cur = map[cur];
  return cur;
}

// Public: user-confirmed merge. `from` is the canonical id of the
// exercise whose history we want absorbed into `to`. Both ids should
// resolve to library or custom entries; we save the mapping and
// callers can re-run the workout-canonicalisation migration to fold
// historical entries into the target.
export function mergeExercise(fromId, toId) {
  if (!fromId || !toId || fromId === toId) return false;
  const map = loadManualMap();
  map[fromId] = toId;
  saveManualMap(map);
  // Bump a tick so the workout-history migration on next launch picks
  // it up and rewrites old entries.
  try {
    const cur = parseInt(localStorage.getItem("riyadahNameMigrationV") || "0", 10);
    localStorage.setItem("riyadahNameMigrationV", String(Math.max(0, cur - 1)));
  } catch {}
  return true;
}
export function listManualAliases() { return loadManualMap(); }
export function clearManualAlias(fromId) {
  const map = loadManualMap();
  if (map[fromId]) { delete map[fromId]; saveManualMap(map); }
}

function loadCustomLib() {
  try {
    const raw = localStorage.getItem(CUSTOM_LIB_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function saveCustomLib(arr) {
  try { localStorage.setItem(CUSTOM_LIB_KEY, JSON.stringify(arr)); } catch {}
}
function customNameMap() {
  const m = new Map();
  for (const e of loadCustomLib()) {
    if (e && e.id && e.name) m.set(normName(e.name), e.id);
  }
  return m;
}
function customIdMap() {
  const m = new Map();
  for (const e of loadCustomLib()) {
    if (e && e.id) m.set(e.id, e);
  }
  return m;
}

// Tolerant lookup: try the literal name, then "DB ↔ Dumbbell" /
// "BB ↔ Barbell" / "BW ↔ Bodyweight" expansions and contractions,
// then singular/plural toggles.
function tryLookup(norm, ...maps) {
  for (const m of maps) if (m.has(norm)) return m.get(norm);
  const variants = [
    norm.replace(/\bdb\b/g, "dumbbell"),
    norm.replace(/\bdumbbell\b/g, "db"),
    norm.replace(/\bbb\b/g, "barbell"),
    norm.replace(/\bbarbell\b/g, "bb"),
    norm.replace(/\bbw\b/g, "bodyweight"),
    norm.replace(/\bbodyweight\b/g, "bw"),
  ];
  for (const v of variants) {
    if (v === norm) continue;
    for (const m of maps) if (m.has(v)) return m.get(v);
  }
  // Singular ↔ plural
  const plural = norm.endsWith("s") ? norm.slice(0, -1) : norm + "s";
  for (const m of maps) if (m.has(plural)) return m.get(plural);
  return null;
}

// Stable slug for genuinely-unknown exercises. Used as both the id and
// (via learnExercise) the custom-library key.
function slugForName(name) {
  return "custom_" + normName(name).replace(/\s+/g, "_");
}

export function resolveExerciseId(name, providedId) {
  // a/b — trust the provided id if it matches any known entry.
  let id = null;
  if (providedId) {
    if (STATIC_ID_TO_ENTRY.has(providedId) || customIdMap().has(providedId)) {
      id = providedId;
    }
  }
  if (!id) {
    const norm = normName(name);
    if (!norm) return followManualAlias(providedId || "custom_unknown");
    // Tier 1: literal-norm + curated alias lookup with DB↔Dumbbell etc.
    const hit = tryLookup(norm, STATIC_NAME_TO_ID, customNameMap());
    if (hit) id = hit;
    // Tier 2: deep-norm — synonym-mapped + modifier-stripped + word-sort.
    // Only fires when Tier 1 missed, so safer matches always win first.
    if (!id) {
      const deep = deepNormName(name);
      if (deep) {
        if (STATIC_DEEP_NAME_TO_ID.has(deep)) id = STATIC_DEEP_NAME_TO_ID.get(deep);
        if (!id) {
          // Deep-norm the user's custom library entries too — catches the
          // case where the FIRST log of a name like "Heavy Sumo Goblet
          // Squats" was learned as a custom exercise, and later a
          // different spelling should fold back into the same canonical
          // (or vice versa).
          for (const e of loadCustomLib()) {
            if (deepNormName(e.name) === deep) { id = e.id; break; }
          }
        }
      }
    }
    if (!id) id = slugForName(name);
  }
  // Manual user-confirmed merges (from the "Merge into…" UI) win last —
  // they override even successful canonical matches when the user has
  // explicitly told the app two ids are the same exercise.
  return followManualAlias(id);
}

export function resolveExerciseEntry(name, providedId) {
  const id = resolveExerciseId(name, providedId);
  return STATIC_ID_TO_ENTRY.get(id) || customIdMap().get(id) || null;
}

export function resolveExerciseName(name, providedId) {
  const e = resolveExerciseEntry(name, providedId);
  return e ? e.name : (name || "Unknown");
}

// Adds an exercise to the local custom library when it doesn't match
// anything static. Called by the workout-save path so that the FIRST
// log of a Steve-named exercise locks in its canonical entry, and all
// future logs / PR aggregations attach to the same row.
//
// meta = { name, id?, primary?, secondary?, muscles?, unit?, type? }
// Returns the canonical id (so callers can stamp it back onto the entry).
export function learnExercise(meta) {
  const name = String(meta?.name || "").trim();
  if (!name) return null;
  // If it already resolves to a static or existing custom entry, no-op.
  const existing = resolveExerciseId(name, meta?.id);
  if (existing && (STATIC_ID_TO_ENTRY.has(existing) || customIdMap().has(existing))) {
    return existing;
  }
  // Genuinely new. Persist with whatever metadata the caller supplied
  // so the UI has muscles/category for grouping. Unknown fields stay
  // blank and the user can edit them later.
  const id = existing || slugForName(name);
  const muscles = meta?.muscles || [meta?.primary, ...(meta?.secondary || [])].filter(Boolean).join(", ") || "";
  const lib = loadCustomLib();
  // Defensive — don't dupe.
  if (lib.some(e => e.id === id)) return id;
  lib.push({
    id,
    name,
    category: meta?.category || (meta?.type === "legs" ? "Legs" : meta?.type === "push" ? "Push" : meta?.type === "pull" ? "Pull" : "Custom"),
    primary: meta?.primary || muscles.split(",")[0] || null,
    secondary: meta?.secondary || null,
    muscles: muscles || null,
    unit: meta?.unit || "kg",
    type: meta?.type || null,
    custom: true,
    addedAt: Date.now(),
  });
  saveCustomLib(lib);
  return id;
}

// Bulk-learn helper for save paths — accepts a workout's exercises[] and
// upserts every unknown into the custom library, returning a new
// exercises[] array with each entry's id/name stamped to the canonical
// form. Callers should persist the returned array.
export function canonicaliseWorkoutExercises(exercises) {
  if (!Array.isArray(exercises)) return exercises;
  return exercises.map(ex => {
    if (!ex || typeof ex !== "object") return ex;
    const id = learnExercise({
      name: ex.name,
      id: ex.id,
      primary: ex.primary,
      secondary: ex.secondary,
      muscles: ex.muscles,
      type: ex.type,
      unit: ex.unit,
      category: ex.category,
    });
    const entry = STATIC_ID_TO_ENTRY.get(id) || customIdMap().get(id);
    return {
      ...ex,
      id,
      // Display name = library/canonical. Keep the user's typed string
      // accessible as `aliasName` in case any surface wants to surface
      // the original.
      name: entry ? entry.name : ex.name,
      aliasName: ex.name !== (entry?.name) ? ex.name : undefined,
      muscles: ex.muscles || entry?.muscles || null,
      type: ex.type || entry?.type || null,
    };
  });
}

// Combined library accessor — used by the picker / pickerExercises
// view so users can see both built-in and custom exercises in one list.
export function getFullExerciseLibrary() {
  return [...EXERCISE_LIBRARY, ...loadCustomLib()];
}
